import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../shared/api/client'
import { useCards } from '../../shared/queries/cards'
import type { TrainerCardProps } from './page'

const RATINGS = [1, 2, 3, 4, 5]

export function TrainerAttemptCard(props: TrainerCardProps) {
  const qc = useQueryClient()
  const { cards } = useCards()
  const cardName = cards.find((c) => c.id === props.cardName)?.name ?? props.cardName

  const [open, setOpen] = useState(!props.collapsed)
  const [draft, setDraft] = useState(props.expertOutput ?? props.modelOutput)
  const [dirty, setDirty] = useState(false)
  const [rating, setRating] = useState(props.rating ?? 0)
  const [comment, setComment] = useState(props.comment)
  const [showFewshot, setShowFewshot] = useState(false)

  const save = useMutation({
    mutationFn: () => api.patch<{ ok: boolean }>(`/trainer/attempts/${props.id}`, { expert_output: draft }),
    onSuccess: () => {
      setDirty(false)
      props.onSaved()
      void qc.invalidateQueries({ queryKey: ['trainer-attempts'] })
    },
  })

  const rate = useMutation({
    mutationFn: () => api.post<{ ok: boolean }>(`/trainer/attempts/${props.id}/rating`, { rating, comment }),
    onSuccess: () => props.onSaved(),
  })

  const approve = useMutation({
    mutationFn: () => api.post<{ ok: boolean }>(`/trainer/attempts/${props.id}/approve`),
    onSuccess: () => props.onSaved(),
  })

  const reject = useMutation({
    mutationFn: () => api.post<{ ok: boolean }>(`/trainer/attempts/${props.id}/reject`, { rating, comment }),
    onSuccess: () => props.onSaved(),
  })

  const draftOnly = props.status === 'draft'

  return (
    <div className="card-panel" style={{ marginBottom: 12 }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div>
          <span className="badge">{cardName}</span>
          <span className={`badge ${props.status === 'approved' ? 'ok' : props.status === 'rejected' ? 'danger' : ''}`}>
            {props.status === 'approved' ? 'одобрено' : props.status === 'rejected' ? 'отклонено' : 'черновик'}
          </span>
          {props.rating != null && <span className="badge">оценка {props.rating}/5</span>}
        </div>
        <div className="muted" style={{ fontSize: 13 }}>
          {new Date(props.createdAt).toLocaleString('ru-RU')}
        </div>
      </div>

      <button className="secondary" style={{ marginTop: 10 }} onClick={() => setOpen((v) => !v)}>
        {open ? 'Свернуть' : 'Развернуть'}
      </button>

      {open && (
        <>
          <div style={{ margin: '10px 0' }}>
            <strong>Ответ модели:</strong>
            <div className="transcript">{props.modelOutput}</div>
            <div className="muted" style={{ fontSize: 12 }}>
              {props.model ?? '—'} · {props.promptVersion ?? '—'}
            </div>
          </div>

          {props.fewshotUsed != null && props.fewshotUsed.length > 0 && (
            <div style={{ margin: '10px 0' }}>
              <button className="secondary" onClick={() => setShowFewshot((v) => !v)}>
                few-shot ({props.fewshotUsed.length})
              </button>
              {showFewshot &&
                props.fewshotUsed.map((text, i) => (
                  <div key={i} className="transcript" style={{ marginTop: 6, opacity: 0.8 }}>
                    {i + 1}) {text}
                  </div>
                ))}
            </div>
          )}

          {draftOnly && (
            <div style={{ marginTop: 10 }}>
              <label className="muted" style={{ display: 'block', marginBottom: 6 }}>
                Ваш разбор (правьте, затем сохраните и одобрите)
              </label>
              <textarea
                rows={8}
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value)
                  setDirty(true)
                }}
                aria-label="Ваш разбор"
              />
              <div className="row" style={{ marginTop: 10 }}>
                <button
                  disabled={save.isPending || !dirty || draft.trim().length < 10}
                  onClick={() => save.mutate()}
                >
                  {save.isPending ? 'Сохраняю…' : 'Сохранить правку'}
                </button>
                <span className="muted" style={{ fontSize: 13 }}>
                  {props.expertOutput ? 'правки сохранены' : 'правка ещё не сохранена'}
                </span>
              </div>
              {save.error && (
                <span className="error-box">{save.error instanceof Error ? save.error.message : 'Ошибка'}</span>
              )}
            </div>
          )}

          {props.expertOutput && (
            <div style={{ margin: '10px 0' }}>
              <strong>Ваш разбор (эталон):</strong>
              <div className="transcript">{props.expertOutput}</div>
            </div>
          )}

          {draftOnly && (
            <div style={{ marginTop: 12 }}>
              <label className="muted" style={{ display: 'block', marginBottom: 6 }}>
                Оценка (1 — плохо, 5 — отлично)
              </label>
              <div className="row">
                {RATINGS.map((r) => (
                  <button
                    key={r}
                    className={rating === r ? 'active' : 'secondary'}
                    onClick={() => setRating(r)}
                    aria-label={`Оценка ${r}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
              <label className="muted" style={{ display: 'block', margin: '10px 0 6px' }}>
                Комментарий
              </label>
              <textarea
                rows={2}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Что хорошо, что нет"
                aria-label="Комментарий"
                maxLength={2000}
              />
              <div className="row" style={{ marginTop: 10 }}>
                <button
                  disabled={rate.isPending || rating === 0 || comment.trim().length === 0}
                  onClick={() => rate.mutate()}
                >
                  Поставить оценку
                </button>
                <button
                  className="ok"
                  disabled={approve.isPending || rating === 0 || comment.trim().length === 0 || !props.expertOutput}
                  onClick={() => approve.mutate()}
                >
                  Одобрить
                </button>
                <button
                  className="danger"
                  disabled={reject.isPending || rating === 0 || comment.trim().length === 0}
                  onClick={() => reject.mutate()}
                >
                  Отклонить
                </button>
              </div>
              {!props.expertOutput && (
                <span className="muted" style={{ fontSize: 13 }}>
                  Для одобрения сначала сохраните правку.
                </span>
              )}
              {(rate.error || approve.error || reject.error) && (
                <span className="error-box">
                  {(rate.error ?? approve.error ?? reject.error) instanceof Error
                    ? (rate.error ?? approve.error ?? reject.error)!.message
                    : 'Ошибка'}
                </span>
              )}
            </div>
          )}

          {props.comment && <div className="muted" style={{ marginTop: 10, fontSize: 13 }}>{props.comment}</div>}
        </>
      )}
    </div>
  )
}
